import { Card, CardContent } from '@/components/ui/card';
import { SFIcon } from '@/components/ui/sf-icon';
import { sfIcons } from '@/lib/sf-icons';
import { cn } from '@/lib/utils';
import { semanticTokens } from '@packages/config/theme';
import { CircleFlag } from 'react-circle-flags';

// Payment rail to country code mapping for flags (lowercase for react-circle-flags)
const RAIL_COUNTRY_MAPPING: Record<string, string> = {
  ACH: 'us',
  SEPA: 'eu',
  'SEPA-INSTANT': 'eu',
  SPEI: 'mx',
  PIX: 'br',
  WIRE: 'un', // United Nations flag for global wire transfers
  SWIFT: 'un', // United Nations flag for global transfers
  UPI: 'in',
  'FASTER-PAYMENTS': 'gb',
  CODI: 'mx',
};

// Get payment rail flag based on name
function getRailFlag(name: string) {
  const upperName = name.toUpperCase();
  const countryCode = RAIL_COUNTRY_MAPPING[upperName];

  if (countryCode) {
    return (
      <CircleFlag
        countryCode={countryCode}
        style={{ width: '100%', height: '100%' }}
      />
    );
  }

  // Fallback: no flag for unknown rails
  return null;
}

interface PaymentRailCardProps {
  name: string;
  description: string;
  eta: string;
  isSelected: boolean;
  onClick: () => void;
}

export function PaymentRailCard({
  name,
  description,
  eta,
  isSelected,
  onClick,
}: PaymentRailCardProps) {
  return (
    <Card
      className={cn(
        "cursor-pointer min-h-[56px]",
        semanticTokens.motion.standard,
        semanticTokens.focus.ring,
        "hover:shadow-md",
        isSelected
          ? cn(semanticTokens.border.focus, 'ring-2 ring-blue-500 ring-offset-2')
          : semanticTokens.border.default,
        semanticTokens.bg.surface,
        semanticTokens.shadow.sm,
        semanticTokens.radius.lg,
        "focus-visible:outline-none"
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      aria-label={`Select ${name} payment method`}
      aria-pressed={isSelected}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onClick();
        }
      }}
    >
      <CardContent className={cn(
        "p-4 flex items-center justify-between",
        semanticTokens.spacing.md
      )}>
        <div className="flex items-center gap-3">
          {/* Payment Rail Flag */}
          <div className={cn(
            "w-10 h-10 lg:w-12 lg:h-12",
            semanticTokens.bg.muted,
            semanticTokens.radius.full,
            "flex items-center justify-center shrink-0 p-2"
          )} aria-hidden="true">
            <div className="w-full h-full flex items-center justify-center">
              {getRailFlag(name)}
            </div>
          </div>

          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <div className={cn(
                semanticTokens.typography.bodyLarge,
                semanticTokens.fg.default
              )}>
                {name}
              </div>
              {isSelected && (
                <SFIcon
                  icon={sfIcons.success}
                  className={cn("w-4 h-4", semanticTokens.positive.accent)}
                  aria-hidden="true"
                />
              )}
            </div>
            <div className={cn(
              semanticTokens.typography.caption,
              semanticTokens.fg.muted
            )}>
              {description}
            </div>
            <div className={cn(
              "flex items-center gap-1 mt-1",
              semanticTokens.typography.caption,
              semanticTokens.fg.subtle
            )}>
              <SFIcon icon={sfIcons.pending} className="w-3 h-3" aria-hidden="true" />
              <span>{eta}</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

